import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import KidsProductCard from '../components/KidsProductCard';
import {
  getAllKidsProducts,
  getKidsProductsByCategory,
  getAllKidsCategories,
  getKidsCategoryDisplayName,
} from '../data/kidsProducts';

const PRICE_RANGES = [
  { label: 'All Prices', min: 0, max: Infinity },
  { label: 'Under ₹500', min: 0, max: 500 },
  { label: '₹500 – ₹700', min: 500, max: 700 },
  { label: '₹700 – ₹1000', min: 700, max: 1000 },
];

const KidsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [priceRange, setPriceRange] = useState(0);

  useEffect(() => {
    setSelectedCategory(searchParams.get('category') || 'all');
  }, [searchParams]);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    category === 'all' ? setSearchParams({}) : setSearchParams({ category });
  };

  const { min, max } = PRICE_RANGES[priceRange];
  const base = selectedCategory === 'all' ? getAllKidsProducts() : getKidsProductsByCategory(selectedCategory);
  const products = base.filter(p => p.price >= min && p.price <= max);
  const categories = getAllKidsCategories();

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <div className="relative h-[300px] md:h-[400px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#4CAF50] to-[#DAA520]">
          <div className="absolute inset-0 bg-black/20" />
        </div>
        <div className="relative h-full flex items-center px-4 md:px-20">
          <div className="max-w-2xl">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Kids Collection</h1>
            <p className="text-white/80 text-lg">Discover adorable outfits for your little ones</p>
          </div>
        </div>
      </div>

      <div className="py-8 px-4 md:px-20">
        <div className="max-w-7xl mx-auto">

          {/* Category Filter */}
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-700 mb-3">Category</h2>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-[#DAA520] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getKidsCategoryDisplayName(cat)}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="mb-6">
            <h2 className="text-base font-semibold text-gray-700 mb-3">Price Range</h2>
            <div className="flex flex-wrap gap-2">
              {PRICE_RANGES.map((range, idx) => (
                <button
                  key={range.label}
                  onClick={() => setPriceRange(idx)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    priceRange === idx
                      ? 'bg-[#4CAF50] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-gray-500 text-sm mb-6">Showing {products.length} products</p>

          {products.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🛍️</div>
              <h3 className="text-xl font-semibold mb-2">No products found</h3>
              <p className="text-gray-600">Try a different category or price range</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map(product => (
                <KidsProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KidsPage;
